import type {
  ColDef,
  EditableCallbackParams,
  ITooltipParams,
  ICellRendererParams,
  CellClassParams,
  ValueGetterParams,
} from "ag-grid-community";
import { isNullish } from "remeda";
import type { TCell, TData } from "@package/grid-utils";

import { columnWidth } from "../../globals";
import { getFormatterWithSeparator, getFormatterWithoutSeparator } from "../../numbers";
import { isListOnlyPermissions } from "../../utils";
import type { TColumnHeaderProps } from "../components";
import {
  formatAdhocSpreadsText,
  formatMonthColumnText,
  timespreadData,
  type MainColumnType,
  mainColumnTypeHeaderLabel,
  mainMonthColumnId,
} from "@package/grid-utils";
import { monthTimespreadValueGetter } from "../periodHelpers";
import { cyclesPeriodsValueGetter, cyclesValueGetter } from "./cycles";
import { throttle } from "../../../utils";

import { suppressKeyboardEvent } from "../keyboard";
import { isDev } from "../../../globals";
import type { TProduct, TStatusMap } from "../../../data";
import type { TAdhocSpreadFormatted } from "../../../data/pages/timespreads/types";

import type { TProductConfigs } from "../../../data/cached";
import { getCurrentRangeSelection } from "../useRangeSelectionChange";
import type {
  PageProductsFragmentFragment,
  ProductUserSettingsFragmentFragment,
} from "../../../__generated__/gql/graphql";
import { NumberCellEditor } from "../components/NumberCellEditor";
import { columnValueFormatter } from "./formatters";
import type { ProcessCellStyleFn } from "../cell-style/useProcessCellStyle";
import type { TTooltipCell } from "../components";
import type { UseQueryResult } from "@tanstack/react-query";

export const curveColumnStack = [
  {
    label: "Parent",
    name: "commodity_parent_group",
    defaultVisible: false,
    groupable: true,
    required: false,
    hoverPosition: null,
    showOnMobile: false,
  },
  {
    label: "Region",
    name: "geographical_region",
    hoverPosition: 4,
    defaultVisible: false,
    groupable: true,
    required: false,
    showOnMobile: false,
  },
  {
    label: "Commodity",
    name: "commodity_group",
    hoverPosition: 3,
    defaultVisible: false,
    groupable: true,
    required: false,
    showOnMobile: false,
  },
  {
    label: "Package",
    hoverPosition: 2,
    name: "package",
    required: false,
    defaultVisible: false,
    showOnMobile: false,
    groupable: false,
  },
  {
    name: "name",
    label: "Display Name",
    showOnMobile: true,
    required: true,
    groupable: false,
    defaultVisible: false,
    hoverPosition: null,
  },
  {
    label: "Curve name",
    name: "description",
    hoverPosition: 1,
    required: false,
    defaultVisible: false,
    showOnMobile: false,
    groupable: false,
  },
  {
    label: "Source",
    name: "source",
    hoverPosition: 5,
    required: false,
    defaultVisible: false,
    showOnMobile: false,
    groupable: false,
  },
  {
    label: "Field name",
    name: "field_name",
    required: false,
    defaultVisible: false,
    hoverPosition: null,
    showOnMobile: false,
    groupable: false,
  },
  {
    label: "Price UOM",
    showOnMobile: true,
    name: "uom",
    defaultVisible: false,
    required: true,
    hoverPosition: null,
    groupable: false,
  },
] as const;

export type CurveColumnStackName = (typeof curveColumnStack)[number]["name"];

type RequiredCurveColumnStack = Exclude<(typeof curveColumnStack)[number], { required: false }>;

type OptionalCurveColumnStack = Exclude<(typeof curveColumnStack)[number], { required: true }>;

export const requiredColumnStack = curveColumnStack.filter((column): column is RequiredCurveColumnStack => {
  return column.required;
});

export const potentiallyInvisibleColumns = curveColumnStack.filter((column): column is OptionalCurveColumnStack => {
  return !column.required;
});

export function formatMainMonthPeriod(
  data: { rowType?: string; id?: string; period?: string | null } | null | undefined,
  spreads: TAdhocSpreadFormatted,
): string {
  if (!data) return "";
  if (data.rowType === "adhoc" && data.id) {
    const spread = spreads[data.id] as TAdhocSpreadFormatted[keyof TAdhocSpreadFormatted] | undefined;
    return (
      formatAdhocSpreadsText(
        spread?.from?.periodValue ?? undefined,
        spread?.to?.periodValue ?? undefined,
        spread?.isStrip,
        spread?.spreadType,
        spread?.middle?.periodValue ?? undefined,
        spread?.middle2?.periodValue ?? undefined,
      ) ?? ""
    );
  }
  return formatMonthColumnText(data.period ?? undefined) ?? "";
}

// If a field is provided, it's a shadow column.
// width only is used for the main column period.
function monthColumnDefs({
  entityId,
  field,
  width,
  spreads,
  mainMonthColumn,
  mainColumnVariant,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  spreads: TAdhocSpreadFormatted;
  mainMonthColumn: boolean;
  mainColumnVariant?: MainColumnType;
  processCellStyle: ProcessCellStyleFn;
}) {
  const variant: MainColumnType = mainMonthColumn ? (mainColumnVariant ?? "period") : "period";

  const variantValueGetter = MAIN_COLUMN_VARIANT_VALUE_GETTERS[variant];

  return {
    colId: field ?? "1",
    field: mainMonthColumnId,
    headerComponentParams: {
      entityId,
      mainMonth: mainMonthColumn,
      mainColumnLabel: mainColumnTypeHeaderLabel(variant),
      gridId: field,
    },
    headerName: "Period",
    width: width ? width : 70,
    minWidth: 70,
    lockPinned: !field,
    pinned: field ? undefined : "left",
    lockPosition: !field,
    headerComponent: "agColumnMonths",
    resizable: true,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
    valueGetter: variantValueGetter,
    valueFormatter: (params) => {
      if (variant === "period" || params.data?.rowType === "adhoc") {
        return formatMainMonthPeriod(params.data, spreads);
      }
      return params.value != null ? params.value.toString() : "";
    },
    cellRenderer: "insertAdhocCellRenderer",
    tooltipComponent: "agTooltipPeriod",
    tooltipValueGetter: (params) => {
      const code = params.node?.data?.code;
      const rowType = params.node?.data?.rowType;
      const isTspd = rowType === "adhoc";
      const isInsertAdhoc = rowType === "insert_adhoc";

      if (isInsertAdhoc) {
        return "Add custom time spread or strip";
      }
      if (isTspd) {
        if (params.valueFormatted?.toString().trim() === "-/-") {
          return "Click here to set a custom Time Spread";
        }
        return params.valueFormatted?.toString() || "Time Spread";
      }
      if (isDev) {
        return `${code} / M${params.node?.rowIndex}`;
      }
      if (code) {
        return code;
      }
    },
    chartDataType: "category",
    cellClass: field ? "shadow-cell" : "main-month-cell",
    cellStyle: field
      ? processCellStyle
      : (params) => {
          const api = params?.api;
          if (!api) return null;

          const cellIndex = params.node.rowIndex;

          const rangeSelection = api.getCellRanges();

          const inRange = rangeSelection?.some((range) => {
            const start = range?.startRow?.rowIndex;
            const end = range?.endRow?.rowIndex;
            if (start == null || end == null || cellIndex == null) return false;
            return cellIndex >= start && cellIndex <= end;
          });

          const borderRight = inRange
            ? "4px solid var(--indicator-colour)"
            : "var(--ag-borders-critical) var(--grid-border)";

          const baseStyle = processCellStyle(params) || {};

          return {
            ...baseStyle,
            borderRight,
          };
        },
  } satisfies ColDef<TData, TCell>;
}

const monthTimespreadValueGetterFromParams = monthTimespreadValueGetter(timespreadData);

function monthTimespreadColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "month-timespread",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "month-timespread",
    },
    headerName: "Spread",
    width: field && width ? width : 70,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnMonthTimespread",
    valueGetter: monthTimespreadValueGetterFromParams,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } satisfies ColDef;
}

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

function weekLabel(start: Date) {
  const end = new Date(start);
  end.setDate(start.getDate() + 4); // Friday
  const monthAbbrev = end.toLocaleDateString("en-GB", {
    month: "short",
  });
  return `${pad2(start.getDate())}-${pad2(end.getDate())}/${monthAbbrev}`;
}

function weekValueGetter(params: ValueGetterParams<TData, string>) {
  if (params.data?.rowType !== "mth") {
    return undefined;
  }
  const rowIdx = params.node?.rowIndex ?? 0;
  const today = new Date();
  const day = today.getDay(); // 0 = Sun, 1 = Mon...
  const monday = new Date(today);
  const offset = day === 0 ? -6 : 1 - day; // if Sunday, go back 6 days
  monday.setHours(0, 0, 0, 0);
  monday.setDate(today.getDate() + offset);
  const start = new Date(monday);
  start.setDate(monday.getDate() + rowIdx * 7);
  return weekLabel(start);
}

function weekColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "week",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "week",
    },
    headerName: "CFD Week",
    width: field && width ? width : 70,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnWeeks",
    valueGetter: weekValueGetter,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } as ColDef;
}

function cyclesColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "cycles",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "cycles",
    },
    headerName: "Cycles Calc",
    width: field && width ? width : 105,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnCycles",
    valueGetter: cyclesValueGetter,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } as ColDef;
}

function cyclesPeriodsColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "cycles-periods",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "cycles-periods",
    },
    headerName: "Cycles Periods",
    width: field && width ? width : 85,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnCyclesPeriods",
    valueGetter: cyclesPeriodsValueGetter,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } as ColDef;
}

function osnHalfValueGetter(params: ValueGetterParams<TData, string>) {
  if (params.data?.rowType !== "mth") {
    return undefined;
  }
  const rowIdx = params.node?.rowIndex ?? 0;
  const half = rowIdx % 2 === 0 ? "1H" : "2H";
  const sourcePeriod = params.api.getDisplayedRowAtIndex(Math.floor(rowIdx / 2))?.data?.period;
  if (!sourcePeriod) {
    return undefined;
  }
  return `${half} ${sourcePeriod}`;
}

function osnHalfColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "osn-half",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "osn-half",
    },
    headerName: "OSN Half",
    width: field && width ? width : 85,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnOsnHalf",
    valueGetter: osnHalfValueGetter,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } as ColDef;
}

function blankColumnDefs(
  { entityId, field, width }: { entityId: string; field?: string; width?: number } = { entityId: "" },
) {
  return {
    colId: field,
    field: "blank",
    headerName: "",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "blank",
    },
    cellClass: "blank-cell",
    valueGetter: () => null,
    width: field && width ? width : 70,
    minWidth: 10,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: null,
    enableValue: false,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } satisfies ColDef;
}

function periodCodeValueGetter(params: ValueGetterParams<TData, string>) {
  const rowType = params.data?.rowType;

  if (rowType === "mth") {
    return params.data?.code || "";
  }

  return params.data?.period || "";
}

function periodCodeColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "period-code",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "period-code",
    },
    headerName: "Code",
    width: field && width ? width : 70,
    cellClass: "shadow-cell",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnPeriodCode",
    valueGetter: periodCodeValueGetter,
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } satisfies ColDef;
}

function countValueGetter(params: ValueGetterParams<TData, string>) {
  if (params.data?.rowType !== "mth") {
    return "";
  }

  const rowIndex = params.node?.rowIndex;
  if (rowIndex !== null && rowIndex !== undefined) {
    return (rowIndex + 1).toString();
  }
  return "";
}

function countColumnDefs({
  entityId,
  field,
  width,
  processCellStyle,
}: {
  entityId: string;
  field?: string;
  width?: number;
  processCellStyle: ProcessCellStyleFn;
}) {
  return {
    colId: field,
    field: "count",
    headerComponentParams: {
      entityId,
      gridId: field,
      shadowType: "count",
    },
    headerName: "Count",
    width: field && width ? width : 70,
    cellClass: "shadow-cell text-right",
    cellStyle: processCellStyle,
    lockPinned: false,
    lockPosition: false,
    resizable: true,
    headerComponent: "agColumnCount",
    valueGetter: countValueGetter,
    type: "rightAligned",
    suppressNavigable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  } satisfies ColDef;
}

const MAIN_COLUMN_VARIANT_VALUE_GETTERS = {
  month_code: periodCodeValueGetter,
  time_spread: monthTimespreadValueGetterFromParams,
  cfd_week: weekValueGetter,
  cycles_calc: cyclesValueGetter,
  cycles_periods: cyclesPeriodsValueGetter,
  osn_half: osnHalfValueGetter,
  count: countValueGetter,
  blank: () => null,
} as Partial<Record<MainColumnType, ColDef<TData, TCell>["valueGetter"]>>;

export const defaultColumnSettings = {
  decimalPlaces: 2,
  thousandsSeparator: false,
  columnWidth,
  increment: 0.25,
};

export function validColumnSettings({
  decimalPlaces,
  thousandsSeparator,
  columnWidth,
  increment,
}: {
  decimalPlaces?: number | null;
  thousandsSeparator?: boolean | null;
  columnWidth?: number | null;
  increment?: number | null;
}) {
  return {
    decimalPlaces: isNullish(decimalPlaces) ? defaultColumnSettings.decimalPlaces : decimalPlaces,
    thousandsSeparator: isNullish(thousandsSeparator) ? defaultColumnSettings.thousandsSeparator : thousandsSeparator,
    columnWidth: isNullish(columnWidth) ? defaultColumnSettings.columnWidth : columnWidth,
    increment: isNullish(increment) ? defaultColumnSettings.increment : increment,
  };
}

// When passed the pageProduct, productSettings, products, and packages, this function will return the correct decimal places for the column, following the priority:
// 1. If the pageProduct has a decimal_places value, use that (user-defined, column-level)
// 2. If the productSettings has a dp value for the product, use that (user-defined, product-level)
// 3. If the product has a dp value, use that (admin-set, product-level)
// 4. If the package has a dp value, use that (admin-set, package-level)
// 5. Otherwise, return defaultColumnSettings.decimalPlaces (default, global-level)
function getDecimalPlaces({
  pageProduct,
  productSettings,
  product,
  packages,
}: {
  pageProduct: PageProductsFragmentFragment;
  productSettings: ReadonlyMap<string, ProductUserSettingsFragmentFragment> | undefined;
  product: TProduct | undefined;
  packages: ReadonlyMap<number, number> | undefined;
}) {
  if (!isNullish(pageProduct.decimal_places)) {
    return pageProduct.decimal_places;
  }

  if (product && productSettings && productSettings.has(product.id)) {
    const productSetting = productSettings.get(product.id);
    if (productSetting && !isNullish(productSetting.dp)) {
      return productSetting.dp;
    }
  }

  if (product && !isNullish(product.dp)) {
    return product.dp;
  }

  if (product && packages && !isNullish(product.package)) {
    const packageId = product.package;
    if (packageId !== undefined && packages.has(packageId)) {
      const packageDp = packages.get(packageId);
      if (!isNullish(packageDp)) {
        return packageDp;
      }
    }
  }

  return defaultColumnSettings.decimalPlaces;
}

export const sharedCellOffsetsArr: Record<string, string[]> = {};

export function genColumnDefs({
  currentPageProducts,
  products,
  mainMonthColumnWidth,
  mainColumnType,
  statusMap,
  displayNegativeBracket,
  flashCellUpdates,
  spreads,
  processCellStyle,
  productSettings,
  packagesDp,
  configs,
}: {
  currentPageProducts: (PageProductsFragmentFragment & TProduct)[];
  products: TProduct[];
  mainMonthColumnWidth: number | undefined;
  mainColumnType: MainColumnType;
  statusMap: TStatusMap;
  displayNegativeBracket: boolean;
  flashCellUpdates: boolean;
  configs: TProductConfigs;
  spreads: TAdhocSpreadFormatted;
  productSettings: UseQueryResult<Map<string, ProductUserSettingsFragmentFragment>, Error>;
  packagesDp: UseQueryResult<Map<number, number>, Error> | undefined;
  processCellStyle: ProcessCellStyleFn;
}): ColDef<TData, TCell>[] {
  const productColDefs: ColDef<TData, TCell>[] = currentPageProducts
    ?.map((pageProduct) => {
      const {
        id,
        grid_id: gridId,
        column_field_selector: columnFieldSelector,
        product_id: productId,
        column_type: columnType,
        column_width: columnWidth,
        thousands_separator: thousandsSeparatorSetting,
        decimal_places: decimalPlacesSetting,
      } = pageProduct;

      // Use grid_id if available, fallback to id for backward compatibility
      const columnId = gridId || id;

      // Shadow columns.
      if (columnType !== "product") {
        switch (columnType) {
          case "month":
            return monthColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              spreads,
              processCellStyle,
              mainMonthColumn: false,
            });
          case "period-code":
            return periodCodeColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "month-timespread":
            return monthTimespreadColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "week":
            return weekColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "cycles":
            return cyclesColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "cycles-periods":
            return cyclesPeriodsColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "osn-half":
            return osnHalfColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "count":
            return countColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
              processCellStyle,
            });
          case "blank":
            return blankColumnDefs({
              entityId: id,
              field: columnId,
              width: columnWidth,
            });
        }
      }

      const product = products?.find((p) => p.id === productId);
      const permissions = product?.packageByPackage.permissions;
      const productConfigs = configs[productId ?? ""];

      const sharedCellOffsets = product?.has_shared_cell
        ? productConfigs?.map((config) => (config.formula === null ? config.offset.toString() : null)).filter(Boolean)
        : [];

      if (product?.id) sharedCellOffsetsArr[product.id] = sharedCellOffsets;

      const isListOnly = isListOnlyPermissions(permissions);

      const status = productId ? statusMap?.[productId] || "listen" : "listen";

      const isEditable = (
        params: EditableCallbackParams<TData, TCell> | ITooltipParams<TData, TCell, unknown>,
        customStatusCheck = false,
      ) => {
        const rowIndex = params?.node?.rowIndex?.toString();

        const editable =
          params?.data?.rowType === "mth" &&
          sharedCellOffsets &&
          rowIndex &&
          (status === "broadcast" || status === "hybrid_broadcast" || status === "private" || customStatusCheck)
            ? sharedCellOffsets.includes(rowIndex)
            : false;
        return editable;
      };

      // Define the tooltip value getter function
      const tooltipValueGetter = (params: ITooltipParams) =>
        ({
          status,
          productId: productId ?? undefined,
          isEditable: Boolean(params.data?.rowType === "mth" && product?.has_shared_cell),
        }) satisfies TTooltipCell;

      // Use the throttled version of the tooltip value getter function
      const throttledTooltipValueGetter = throttle(tooltipValueGetter, 1000);

      let { thousandsSeparator, decimalPlaces } = validColumnSettings({
        thousandsSeparator: thousandsSeparatorSetting,
        decimalPlaces: getDecimalPlaces({
          pageProduct,
          productSettings: productSettings.data,
          product,
          packages: packagesDp?.data,
        }),
      });

      const isCannedOrSourced = product?.artis_type === "canned" || product?.artis_type === "sourced";
      const isFpOrTs = product?.maturity === "future" || product?.maturity === "inter_month_spreads";

      const volumeAndOiSelectorsForThousandsSeparator = new Set([
        "oi",
        "oichg",
        "volm",
        "volmprior",
        "bidsize",
        "asksize",
      ]);

      const isVolumeOrOiSelector =
        !!columnFieldSelector && volumeAndOiSelectorsForThousandsSeparator.has(columnFieldSelector);

      if (isCannedOrSourced && isFpOrTs && isVolumeOrOiSelector) {
        thousandsSeparator = true;
        decimalPlaces = 0;
      }

      const formatter = thousandsSeparator
        ? getFormatterWithSeparator(decimalPlaces)
        : getFormatterWithoutSeparator(decimalPlaces);

      return {
        colId: columnId,
        field: columnId,
        headerName: product?.name,
        suppressFillHandle: !product?.has_shared_cell,
        ...(!isListOnly &&
          permissions && {
            tooltipComponent: "agTooltipProduct",
            // Header tooltips are now handled by custom tooltip in ColumnHeaderProduct
            // params for row cell tooltip
            tooltipValueGetter: throttledTooltipValueGetter,
            editable: isEditable,
          }),
        type: "numericColumn",
        cellEditor: NumberCellEditor,
        headerComponent: "agColumnHeaderProducts",
        cellRendererSelector: (params: ICellRendererParams) => {
          const productId = params.colDef?.headerComponentParams.productId;
          const sharedCells = sharedCellOffsetsArr[productId];
          const rowIndex = params.node.rowIndex?.toString();
          if (flashCellUpdates && rowIndex && productId && sharedCells?.includes(rowIndex)) {
            return {
              component: "flashCellRenderer",
            };
          }
          return undefined;
        },
        // anything changing in header params (local or remote changes),
        // will lead to the column headers being re-rendered.
        headerComponentParams: {
          entityId: id,
          gridId: columnId,
          productId: productId ?? undefined,
          packageId: product?.package ?? undefined,
          field: columnFieldSelector ?? undefined,
          settings: validColumnSettings({
            thousandsSeparator: thousandsSeparatorSetting,
            decimalPlaces: decimalPlacesSetting,
          }),

          status: status || "listen",
          artisType: product?.artis_type,
          sharedCellOffsets,
          hasSharedCell: Boolean(product?.has_shared_cell),
          permissions,
          exchange: product?.packageByPackage.sourceBySource?.exchange?.code,
          maturity: product?.maturity,
          columnLabels: {
            commodityParentGroup:
              product?.commodityGroupByCommodityGroup?.commodity_parent_group?.commodity_parent_group,
            commodityGroup: product?.commodity_group,
            geographicalRegion: product?.geographical_region,
            package: product?.packageByPackage?.name,
            uom: product?.uom,
            uomDisplay: product?.product_uom?.description,
            description: product?.description,
            source: product?.packageByPackage?.sourceBySource?.name,
          },
          hasFormula: (productConfigs?.map((config) => config?.formula).filter(Boolean) || [])?.length > 0,
        } satisfies TColumnHeaderProps,
        suppressHeaderMenuButton: true,
        suppressKeyboardEvent,
        filter: false,
        sortable: false,
        cellClass: "ag-right-aligned-cell",
        cellStyle: (params) => processCellStyle(params, product),
        cellClassRules: {
          rangeSelectionGroupTwo: (params) => findRangeSelectionCell(params, 1),
        },
        valueFormatter: columnValueFormatter({
          columnFieldSelector,
          displayNegativeBracket,
          formatter,
          uom: product?.uom,
          productSettingsLoading: productSettings.isLoading && isNullish(decimalPlacesSetting),
        }),
        chartDataType: "series", // Ensure AG Grid treats this as series data even if first row is null

        width: columnWidth || 150,
      } satisfies ColDef<TData, TCell>;
    })
    .filter(Boolean) as ColDef<TData, TCell>[];

  return currentPageProducts
    ? [
        monthColumnDefs({
          entityId: "main-month",
          width: mainMonthColumnWidth,
          spreads,
          processCellStyle,
          mainMonthColumn: true,
          mainColumnVariant: mainColumnType,
        }),
        ...productColDefs,
      ]
    : [];
}

export function findRangeSelectionCell(params: CellClassParams, group: number) {
  const rangeSelection = getCurrentRangeSelection();
  // only check for match in array gorup
  if (rangeSelection.length <= group) return false;
  if (rangeSelection.length > 2) return false;
  for (let i = 0; i < rangeSelection[group].length; i++) {
    if (
      rangeSelection[group][i].rowId === params.data.id &&
      rangeSelection[group][i].columnId === params.colDef.field
    ) {
      return true;
    }
  }

  return false;
}
